import { InputEvents, Metrics, ObjectState, Surface, Obj } from "../types";
import { CreateObjectArgs, createSprite } from "../core";

type CreateGameOfLifeArgs = CreateObjectArgs & {
  w?: number;
  h?: number;
}

export const createGameOfLife = (args: CreateGameOfLifeArgs) => {
  // Initialize state: 0 = dead, 1 = alive
  const initSprite = (w: number = 0, h: number = 0) => {
    const sprite = createSprite({ w, h, fill: 0 });
    // Random initial state
    for (let i = 0; i < sprite.c.length; i++) {
      sprite.c[i] = Math.random() > 0.7 ? 1 : 0;
    }
    return sprite;
  }

  let sprite = initSprite(args.w, args.h);

  const gol = {
    ...args,
    sprite,
    step: (
      startTime: number,
      timestamp: number,
      inputEvents: InputEvents,
      metrics: Metrics,
      inFocus: boolean,
      objectState: ObjectState,
      surface: Surface,
      obj: Obj
    ) => {
      const w = obj.w;
      const h = obj.h;
      // Compute next state
      if (sprite.w !== w || sprite.h !== h) {
        // Reset state if dimensions changed
        sprite = initSprite(w, h);
      }
      const nextSprite = createSprite({ w, h, fill: 0 });

      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          let alive = 0;
          // Count neighbors
          for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
              if (dx === 0 && dy === 0) continue;
              const nx = (x + dx + w) % w;
              const ny = (y + dy + h) % h;
              alive += sprite.c[ny * w + nx];
            }
          }
          const idx = y * w + x;
          if (sprite.c[idx]) {
            // If cell is currently alive and has 2 or 3 live neighbors, it stays alive; otherwise, it dies.
            nextSprite.c[idx] = alive === 2 || alive === 3 ? 1 : 0;
          } else {
            // If cell is currently dead and has exactly 3 live neighbors, it becomes alive; otherwise, it stays dead.
            nextSprite.c[idx] = alive === 3 ? 1 : 0;
          }
        }
      }

      if (inputEvents.clickDetected && inFocus) {
        const mouseX  = inputEvents.mouseX;
        const mouseY = inputEvents.mouseY;
        // Update cell under mouse cursor to be alive
        if (mouseX >= obj.x && mouseX < obj.x + w && mouseY >= obj.y && mouseY < obj.y + h) {
          const cellX = mouseX - obj.x;
          const cellY = mouseY - obj.y;
          const idx = cellY * w + cellX;
          nextSprite.c[idx] = 1;
        }
      }

      sprite = nextSprite;
      obj.sprite = sprite;
    }
  };

  return gol;
};
